import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { ArrowRight, Loader2, Eye, EyeOff, AlertCircle, CheckCircle2, Circle, ShieldCheck } from "lucide-react";

function checks(pwd) {
  return {
    length: pwd.length >= 6,
    letter: /[A-Za-z\u0600-\u06FF]/.test(pwd),
    digit: /\d/.test(pwd),
  };
}

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const c = useMemo(() => checks(password), [password]);
  const ok = c.length && c.letter && c.digit && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!ok) { setError("تحقّق من متطلبات كلمة المرور والتطابق"); return; }
    setLoading(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("تم تحديث كلمة المرور — سجّل دخولك الآن");
      navigate("/login", { replace: true });
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen-safe grid place-items-center cream-gradient p-4" data-testid="reset-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-teal-700 mb-6">
          <ArrowRight className="h-4 w-4" /> العودة لتسجيل الدخول
        </Link>

        <div className="bg-white rounded-2xl border border-cream-200 card-shadow p-5">
          <Link to="/" className="mb-6 block"><Logo variant="navy" size={96} /></Link>

          <div className="h-14 w-14 rounded-full bg-teal-50 grid place-items-center mb-4">
            <ShieldCheck className="h-7 w-7 text-teal-700" />
          </div>
          <h1 className="text-2xl font-heading font-black text-slate-900">اختيار كلمة مرور جديدة</h1>
          <p className="mt-2 text-slate-600 text-sm">أدخل كلمة المرور الجديدة أدناه.</p>

          <form onSubmit={submit} noValidate className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">كلمة المرور الجديدة</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"} required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 pe-12 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                  data-testid="reset-password-input"
                  dir="ltr"
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 end-3 my-auto h-8 w-8 grid place-items-center rounded-md text-slate-500 hover:text-teal-700 hover:bg-slate-100">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">تأكيد كلمة المرور</label>
              <input
                type={show ? "text" : "password"} required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                data-testid="reset-confirm-input"
                dir="ltr"
              />
            </div>

            <div className="rounded-lg bg-cream-50 border border-cream-200 p-3 text-xs">
              <div className="font-semibold text-slate-700 mb-2">متطلبات كلمة المرور:</div>
              <ul className="grid gap-1.5">
                <Row ok={c.length} text="6 أحرف على الأقل" />
                <Row ok={c.letter} text="حرف واحد على الأقل" />
                <Row ok={c.digit} text="رقم واحد على الأقل" />
                <Row ok={!!password && password === confirm} text="التطابق مع التأكيد" />
              </ul>
            </div>

            {error && (
              <div role="alert" className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100 flex items-start gap-2" data-testid="reset-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><span>{error}</span>
              </div>
            )}

            <button
              type="submit" disabled={loading || !ok}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60"
              data-testid="reset-submit"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
              تحديث كلمة المرور
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Row({ ok, text }) {
  const Icon = ok ? CheckCircle2 : Circle;
  return (
    <li className={`flex items-center gap-2 ${ok ? "text-emerald-700" : "text-slate-500"}`}>
      <Icon className="h-3.5 w-3.5" /> <span>{text}</span>
    </li>
  );
}
